import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { Package, Search, Loader2, AlertCircle, ArrowLeft, Truck, CheckCircle, X, Box, Printer } from 'lucide-react';

interface ConsolidationOrder {
  id: number;
  orderNum: string;
  createdAt: number;
  deliveryDate: string;
  deliveryTime?: string;
  estate: string;
  address: string;
  customerName: string;
  phone: string;
  phoneMasked: string;
  totalPrice: number;
  voucherDiscount: number;
  payable: number;
  paymentConfirmed: boolean;
  orderCompleted: boolean;
  deliveredAt?: number;
  orderType: 'delivery' | 'pickup';
  items: Array<{ name: string; quantity: number; unitPrice: number }>;
}

interface Consolidation {
  id: number;
  consolNum: string;
  merchantCode: string;
  merchantName: string;
  ownerPhone: string;
  expectedDeliveryDate: string;
  status: 'pending' | 'preparing' | 'consolidated' | 'picked_up' | 'delivered' | 'cancelled';
  pickupMethod: 'self' | 'logistics';
  pickupTime?: string;
  logisticsType?: 'sf_direct' | 'direct_to_owner' | null;
  logisticsCompany?: string;
  logisticsContact?: string;
  deliveryAddress?: string;
  adminRemarks?: string;
  orderCount: number;
  totalQty: number;
  createdAt: number;
  updatedAt: number;
}

interface ConsolidationDetail {
  consolidation: Consolidation;
  orders: ConsolidationOrder[];
  itemsSummary: string;
  totalQty: number;
  orderCount: number;
}

const STATUS_LABELS: Record<string, string> = {
  pending: '待備貨',
  consolidated: '合併完',
  picked_up: '已提貨',
  delivered: '客戶收貨',
  cancelled: '已取消',
};

const STATUS_BADGES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300',
  consolidated: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
  picked_up: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300',
  delivered: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300',
};

function formatDate(ts?: number): string {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleDateString('zh-HK');
}

function formatCurrency(n: number): string {
  return `HK$${Number(n || 0).toLocaleString('zh-HK')}`;
}

function escapeHtml(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const AdminConsolidations: React.FC = () => {
  const { consolNum } = useParams<{ consolNum?: string }>();
  const navigate = useNavigate();

  const [consolidations, setConsolidations] = useState<Consolidation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [merchantCodeFilter, setMerchantCodeFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [detail, setDetail] = useState<ConsolidationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [updateLoading, setUpdateLoading] = useState(false);
  const [adminRemarks, setAdminRemarks] = useState('');

  const loadConsolidations = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (merchantCodeFilter.trim()) params.set('merchantCode', merchantCodeFilter.trim());
      if (phoneFilter.trim()) params.set('phone', phoneFilter.trim());
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const res = await apiFetch(`/api/public/admin/consolidations?${params.toString()}`);
      if (!res.ok) throw new Error(`載入失敗 (${res.status})`);
      const data = await res.json();
      setConsolidations(data.consolidations || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (num: string) => {
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await apiFetch(`/api/public/admin/consolidations/${encodeURIComponent(num)}`);
      if (!res.ok) throw new Error(`載入失敗 (${res.status})`);
      const data = await res.json();
      setDetail(data as ConsolidationDetail);
      setAdminRemarks(data.consolidation.adminRemarks || '');
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : '載入失敗');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadConsolidations();
  }, [statusFilter, merchantCodeFilter, phoneFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (consolNum) {
      loadDetail(consolNum);
    } else {
      setDetail(null);
    }
  }, [consolNum]);

  const handleUpdateStatus = async (newStatus: string) => {
    if (!detail) return;
    const isStatusChange = newStatus !== detail.consolidation.status;
    if (isStatusChange && !window.confirm(`確認將送貨單 ${detail.consolidation.consolNum} 更新為「${STATUS_LABELS[newStatus]}」？`)) return;

    setUpdateLoading(true);
    try {
      const res = await apiFetch(`/api/public/admin/consolidations/${encodeURIComponent(detail.consolidation.consolNum)}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          adminRemarks: adminRemarks.trim() || null,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || '更新失敗');
      await loadDetail(detail.consolidation.consolNum);
      loadConsolidations();
    } catch (e) {
      alert(e instanceof Error ? e.message : '更新失敗');
    } finally {
      setUpdateLoading(false);
    }
  };

  // 列印執貨單：開新視窗輸出乾淨排版（商品總匯 + 每張訂單嘅商品/地址），方便執貨分類
  const handlePrint = () => {
    if (!detail) return;
    const c = detail.consolidation;
    const esc = escapeHtml;
    const orderRows = detail.orders.map((o) => {
      const items = (o.items || [])
        .map((it) => `<li>${esc(it.name)} <b>× ${esc(it.quantity)}</b></li>`)
        .join('');
      return `<tr>
        <td><b>#${esc(o.orderNum)}</b></td>
        <td>${esc(o.customerName || '—')}<br/><span class="muted">${esc(o.phoneMasked || '')}</span></td>
        <td><ul>${items}</ul></td>
        <td>${esc(o.estate || '')}</td>
        <td>${esc(o.address || '').replace(/\n/g, '<br/>')}</td>
        <td>${o.deliveredAt ? '已送達' : '待送達'}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="zh-HK"><head><meta charset="utf-8"/>
<title>執貨單 ${esc(c.consolNum)}</title>
<style>
  body { font-family: -apple-system, "PingFang HK", "Microsoft JhengHei", sans-serif; color:#111; margin:24px; font-size:13px; }
  h1 { font-size:20px; margin:0 0 4px; }
  .meta { color:#444; margin-bottom:16px; line-height:1.7; }
  .summary { border:1px solid #999; border-radius:8px; padding:10px 14px; margin-bottom:18px; white-space:pre-wrap; }
  table { width:100%; border-collapse:collapse; }
  th, td { border:1px solid #999; padding:6px 8px; text-align:left; vertical-align:top; }
  th { background:#eee; }
  ul { margin:0; padding-left:16px; }
  .muted { color:#666; font-size:12px; }
  h2 { font-size:15px; margin:0 0 8px; }
  @media print { body { margin:8mm; } }
</style></head><body>
<h1>好餸社企 — 送貨單</h1>
<div class="meta">
  <b>${esc(c.consolNum)}</b>（${esc(STATUS_LABELS[c.status] || c.status)}）<br/>
  商家：${esc(c.merchantName)}（${esc(c.merchantCode)}） · 聯絡：${esc(c.ownerPhone || '—')}<br/>
  期望提貨或送貨日期：${esc(c.expectedDeliveryDate)} · 提貨方式：${c.pickupMethod === 'logistics' ? (c.logisticsType === 'sf_direct' ? '物流送货 — 順豐直送' : c.logisticsType === 'direct_to_owner' ? `物流送货 — 直送店主（${esc(c.deliveryAddress || '')}）` : '物流送货') : `店主自提${c.pickupTime ? ` · ${esc(c.pickupTime)}` : ''}`}<br/>
  訂單：${detail.orderCount} 張 · 合共 ${detail.totalQty} 份
</div>
<h2>商品總匯</h2>
<div class="summary">${esc(detail.itemsSummary || '—')}</div>
<h2>訂單明細</h2>
<table>
  <thead><tr><th>訂單</th><th>客戶</th><th>商品內容</th><th>屋苑</th><th>地址</th><th>送達</th></tr></thead>
  <tbody>${orderRows}</tbody>
</table>
<script>window.onload = function(){ window.print(); };</script>
</body></html>`;

    const win = window.open('', '_blank');
    if (!win) {
      alert('瀏覽器封鎖咗彈出視窗，請允許彈窗後再試');
      return;
    }
    win.document.write(html);
    win.document.close();
  };

  const renderFilters = () => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">狀態</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          >
            <option value="">全部</option>
            <option value="pending">待備貨</option>
            <option value="consolidated">合併完</option>
            <option value="picked_up">已提貨</option>
            <option value="delivered">客戶收貨</option>
            <option value="cancelled">已取消</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">商家 GS Code</label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={merchantCodeFilter}
              onChange={(e) => setMerchantCodeFilter(e.target.value)}
              placeholder="輸入 GS Code"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">商家手機號碼</label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={phoneFilter}
              onChange={(e) => setPhoneFilter(e.target.value.replace(/\D/g, ''))}
              placeholder="輸入商家手機"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">期望提貨或送貨日期 起</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">期望提貨或送貨日期 止</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          />
        </div>
      </div>
    </div>
  );

  const renderList = () => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {consolidations.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          {loading ? <Loader2 size={20} className="animate-spin mx-auto mb-2" /> : '沒有符合條件的送貨單'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">送貨單號</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">商家</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">商家手機</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">期望提貨或送貨日期</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">訂單/件數</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">狀態</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {consolidations.map((c) => (
                <tr key={c.consolNum} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 align-top">
                    <div className="font-bold text-gray-900 dark:text-white">{c.consolNum}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{formatDate(c.createdAt)}</div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="text-gray-900 dark:text-white">{c.merchantName}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{c.merchantCode}</div>
                  </td>
                  <td className="px-4 py-3 align-top font-mono text-gray-700 dark:text-gray-300">
                    {c.ownerPhone || '—'}
                  </td>
                  <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-300">{c.expectedDeliveryDate}</td>
                  <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-300">
                    {c.orderCount || 0} 張 / {c.totalQty || 0} 份
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${STATUS_BADGES[c.status]}`}>
                      {STATUS_LABELS[c.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <button
                      onClick={() => navigate(`/admin/consolidations/${c.consolNum}`)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/20 dark:text-orange-300 transition"
                    >
                      查看
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderDetail = () => {
    if (detailLoading) return <Loader2 size={24} className="animate-spin mx-auto" />;
    if (detailError) return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
        <AlertCircle size={18} className="shrink-0 mt-0.5" />
        <div>{detailError}</div>
      </div>
    );
    if (!detail) return null;

    const c = detail.consolidation;

    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/admin/consolidations')}
          className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400"
        >
          <ArrowLeft size={16} /> 返回列表
        </button>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{c.consolNum}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {c.merchantName} · {c.merchantCode} · {c.ownerPhone}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                期望提貨或送貨日期：{c.expectedDeliveryDate}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-bold ${STATUS_BADGES[c.status]}`}>
                {STATUS_LABELS[c.status]}
              </span>
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-bold ${c.pickupMethod === 'logistics' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}>
                {c.pickupMethod === 'logistics' ? '物流送货' : c.pickupTime ? `店主自提 · ${c.pickupTime}` : '店主自提'}
              </span>
              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-gray-800 hover:bg-gray-900 text-white dark:bg-gray-600 dark:hover:bg-gray-500 transition"
              >
                <Printer size={14} /> 列印執貨單
              </button>
            </div>
          </div>

          {/* Action buttons */}
          {c.status !== 'delivered' && c.status !== 'cancelled' && (
            <div className="flex flex-wrap gap-2 mb-6">
              {c.status === 'pending' && (
                <button
                  onClick={() => handleUpdateStatus('consolidated')}
                  disabled={updateLoading}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                >
                  <Box size={16} /> 合併完成
                </button>
              )}
              {c.status === 'consolidated' && (
                <button
                  onClick={() => handleUpdateStatus('picked_up')}
                  disabled={updateLoading}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                >
                  <Truck size={16} /> 已提貨
                </button>
              )}
              <button
                onClick={() => handleUpdateStatus('cancelled')}
                disabled={updateLoading}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-300 disabled:opacity-50"
              >
                <X size={16} /> 取消送貨單
              </button>
            </div>
          )}

          {/* 提貨/送貨詳情（由鄰里店於商家後台決定，admin 只查看） & admin remarks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">提貨方式</label>
              <div className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 text-sm">
                {c.pickupMethod === 'logistics' ? '物流送货' : c.pickupTime ? `店主自提 · ${c.pickupTime}` : '店主自提'}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">運輸詳情（由鄰里店填寫）</label>
              <div className={`w-full p-2.5 border rounded-lg text-sm ${c.pickupMethod === 'logistics' && c.logisticsType !== 'direct_to_owner' && !c.logisticsCompany && c.status === 'consolidated' ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20' : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900'}`}>
                {c.pickupMethod !== 'logistics' ? (
                  <span className="text-gray-400">—</span>
                ) : c.logisticsType === 'sf_direct' ? (
                  <span className="text-blue-700 dark:text-blue-300 font-bold">順豐直送</span>
                ) : c.logisticsType === 'direct_to_owner' ? (
                  <span className="text-gray-700 dark:text-gray-300">
                    <span className="font-bold">直送店主</span>
                    <br />
                    地址：{c.deliveryAddress || '—'}
                    <br />
                    物流：由發貨方安排順豐
                  </span>
                ) : c.logisticsCompany ? (
                  <span className="text-gray-700 dark:text-gray-300">{c.logisticsCompany} · {c.logisticsContact}</span>
                ) : (
                  <span className="text-orange-600 dark:text-orange-400 font-bold">鄰里店尚未填寫</span>
                )}
              </div>
              {c.pickupMethod === 'logistics' && c.logisticsType !== 'direct_to_owner' && !c.logisticsCompany && c.status === 'consolidated' && (
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 font-bold">
                  合併完成，請通知鄰里店登入商家後台補充物流資料。
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">管理備註</label>
              <input
                type="text"
                value={adminRemarks}
                onChange={(e) => setAdminRemarks(e.target.value)}
                disabled={c.status === 'delivered' || c.status === 'cancelled'}
                placeholder="內部備註"
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm disabled:opacity-60"
              />
            </div>
          </div>

          {/* Items summary */}
          <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4 mb-6">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-2">商品總匯（{detail.totalQty} 份）</h3>
            <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans">{detail.itemsSummary || '—'}</pre>
          </div>

          {/* Orders table */}
          <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-2">訂單明細（{detail.orderCount} 張）</h3>
          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">訂單</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">客戶</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">商品內容</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">屋苑</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">地址</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">送達狀態</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">金額</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {detail.orders.map((o) => (
                  <tr key={o.orderNum} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 align-top">
                      <div className="font-bold text-gray-900 dark:text-white">#{o.orderNum}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{formatDate(o.createdAt)}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-gray-900 dark:text-white">{o.customerName || '—'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{o.phoneMasked}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <ul className="space-y-0.5 text-gray-700 dark:text-gray-300">
                        {(o.items || []).map((it, idx) => (
                          <li key={idx} className="text-xs">
                            <span className="font-bold">{it.name}</span>
                            <span className="text-gray-500 dark:text-gray-400"> × {it.quantity}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-300">{o.estate}</td>
                    <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-300 whitespace-pre-line">{o.address}</td>
                    <td className="px-4 py-3 align-top">
                      {o.deliveredAt ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-green-600 dark:text-green-400">
                          <CheckCircle size={12} /> 已送達
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">待送達</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="font-bold text-gray-900 dark:text-white">{formatCurrency(o.payable)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Package className="text-orange-600" size={28} />
            備貨集運
          </h1>
        </div>

        {consolNum ? renderDetail() : (
          <>
            {renderFilters()}
            {loading && consolidations.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : error ? (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
                <AlertCircle size={18} className="shrink-0 mt-0.5" />
                <div>{error}</div>
              </div>
            ) : renderList()}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminConsolidations;
